import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { fetchSessionData, fetchCompletedExams, fetchExams, fetchUserById, updateManagerExplanations } from "../utils/api";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Notification from "./Notification";
import "./CompletedExams.css";

function CompletedExams() {
  // ... your state + hooks, same as before ...
  const { token } = useAuth();
  const location = useLocation();
  const sessionId = location.state?.sessionId;
  const [rows, setRows] = useState([]);
  const [examList, setExamList] = useState([]);
  const [dbUser, setDbUser] = useState(null);
  const [managerExplanations, setManagerExplanations] = useState({});
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [examSession, setExamSession] = useState(null);

  useEffect(() => {
    fetchExams(token)
      .then((data) => setExamList(data || []))
      .catch((err) =>
        setNotification({
          message: "Failed to fetch exam list.",
          type: "error",
        })
      );

    if (sessionId) {
      fetchCompletedExams(sessionId, token)
        .then((data) => {
          setRows(data || []);
          const initialExplanations = {};
          (data || []).forEach((row) => {
            if (row.manager_explanation) {
              initialExplanations[row.id] = row.manager_explanation;
            }
          });
          setManagerExplanations(initialExplanations);
        })
        .catch((err) => {
          console.error("Error loading completed exams:", err);
          setNotification({
            message: "Failed to load completed exams.",
            type: "error",
          });
        });
    }
  }, [sessionId, token]);

  // Identify exam & user from first row
  const examId = rows.length > 0 ? parseInt(rows[0].exam_id, 10) : null;
  const userId = rows.length > 0 ? parseInt(rows[0].user_id, 10) : null;

  // Once we know userId, fetch single user
  useEffect(() => {
    if (userId) {
      fetchUserById(userId, token)
        .then((userData) => setDbUser(userData))
        .catch((err) =>
          setNotification({
            message: "Failed to fetch user info.",
            type: "error",
          })
        );
    }
  }, [userId, token]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionData(sessionId, token)
        .then((data) => setExamSession(data))
        .catch((err) => {
          console.error("Error fetching exam session:", err);
          setNotification({
            message: "Failed to fetch exam session.",
            type: "error",
          });
        });
    }
  }, [sessionId, token]);

  console.log("CHECK HERE", examSession && examSession.end_time
    ? new Date(examSession.end_time).toLocaleString()
    : "Loading...");

  const matchingExam = examList.find((ex) => ex.id === examId);
  const examName = matchingExam ? matchingExam.exam_name : "Unknown Exam";
  const employeeName = dbUser ? dbUser.name : "Unknown User";

  
  // Group data by type
  const groupedData = useMemo(() => {
    let all = [];
    let currentHeader = null;
    let currentSection = null;
    let scenarioList = [];

    rows.forEach((r) => {
      switch (r.question_type) {
        case "H":
          currentHeader = r.question_text;
          currentSection = null;
          scenarioList = [];
          all.push({ type: "H", text: currentHeader });
          break;
        case "S":
          currentSection = r.question_text;
          scenarioList = [];
          all.push({ type: "S", text: currentSection });
          break;
        case "Q":
          scenarioList.push(r.question_text);
          break;
        case "T":
        case "FA":
          all.push({
            type: r.question_type,
            header: currentHeader,
            section: currentSection,
            scenarios: [...scenarioList],
            rowData: r,
          });
          break;
        default:
          break;
      }
    });
    return all;
  }, [rows]);

  // Overall & header stats
  const { overallScore, headerScores } = useMemo(() => {
    let totalTFA = 0;
    let correctTFA = 0;
    let statsByHeader = {};

    groupedData.forEach((block) => {
      if (block.type === "T" || block.type === "FA") {
        totalTFA++;
        const { rowData } = block;
        const finalCorrect = rowData.is_correct_first || rowData.is_correct_second;
        if (finalCorrect) correctTFA++;

        const headerName = block.header || "No Header";
        if (!statsByHeader[headerName]) {
          statsByHeader[headerName] = { total: 0, correct: 0 };
        }
        statsByHeader[headerName].total++;
        if (finalCorrect) statsByHeader[headerName].correct++;
      }
    });

    const overallScore = totalTFA === 0 ? 0 : (correctTFA / totalTFA) * 100;
    return { overallScore, headerScores: statsByHeader };
  }, [groupedData]);

  // Check final-incorrect T questions
  const anyFinalIncorrect = useMemo(() => {
    return groupedData.some((block) => {
      if (block.type === "T") {
        const { rowData } = block;
        return !rowData.is_correct_first && !rowData.is_correct_second;
      }
      return false;
    });
  }, [groupedData]);

  const showSaveButton = overallScore > 50 && anyFinalIncorrect;

  // Save Explanations
  const handleSaveExplanations = async () => {
    try {
      const payload = [];
      rows.forEach((row) => {
        const explanation = managerExplanations[row.id];
        if (typeof explanation === "string") {
          payload.push({
            id: row.id,
            manager_explanation: explanation.trim(),
          });
        }
      });
      if (payload.length === 0) {
        setNotification({
          message: "No explanations to save.",
          type: "error",
        });
        return;
      }

      await updateManagerExplanations(payload, token);
      setNotification({
        message: "Explanations saved successfully!",
        type: "success",
      });

      // re-fetch from DB
      const updated = await fetchCompletedExams(sessionId, token);
      setRows(updated || []);
      const newExplanations = {};
      (updated || []).forEach((row) => {
        if (row.manager_explanation) {
          newExplanations[row.id] = row.manager_explanation;
        }
      });
      setManagerExplanations(newExplanations);

    } catch (err) {
      console.error("Failed to save manager explanations:", err);
      setNotification({
        message: "Error saving explanations.",
        type: "error",
      });
    }
  };

  // Download PDF
const handleDownloadPDF = async () => {
  try {
    const container = document.querySelector(".completed-exams-container");
    if (!container) {
      alert("Cannot find .completed-exams-container");
      return;
    }

    const today = new Date().toISOString().split("T")[0]; // e.g. 2023-08-30
    const safeEmployeeName = employeeName.replace(/\s+/g, "_");
    const safeExamName = examName.replace(/\s+/g, "_");
    const fileName = `${safeEmployeeName}_${safeExamName}_${today}.pdf`;

    // 1) Create a lower-res canvas of the entire container
    const canvas = await html2canvas(container, {
      scale: 1, // reduced from 2 to 1 for smaller image size
      ignoreElements: (element) =>
        element.classList && element.classList.contains("no-pdf"),
    });

    // 2) Convert canvas to JPEG image with compression
    const imgData = canvas.toDataURL("image/jpeg", 0.7); // 70% quality JPEG

    // 3) Log image size before embedding into PDF
    const imageSizeInMB = (imgData.length * (3 / 4)) / 1024 / 1024;
    console.log(`Image size: ${imageSizeInMB.toFixed(2)} MB`);

    // 4) Prepare jsPDF
    const pdf = new jsPDF("p", "pt", "a4"); // 595 x 842 in 'pt' units
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const ratio = canvasHeight / canvasWidth;
    const printedWidth = pageWidth;
    const printedHeight = pageWidth * ratio;

    // 5) Add image to PDF
    if (printedHeight <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, printedWidth, printedHeight);
    } else {
      let currentHeight = 0;
      let pageIndex = 0;

      while (currentHeight < printedHeight) {
        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0 - pageHeight * pageIndex,
          printedWidth,
          printedHeight
        );

        currentHeight += pageHeight;
        pageIndex++;
      }
    }

    // 6) Save the PDF
    pdf.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Failed to generate PDF.");
  }
};

  function autoResizeTextarea(e) {
    const textArea = e.target;
    textArea.style.height = "auto";
    textArea.style.height = `${textArea.scrollHeight}px`;
  }

  return (
    <div className="completed-exams-container">
      {/* 1) Render notification if there's a message */}
      {notification.message && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ message: "", type: "" })}
        />
      )}
      
      {/* Download PDF button */}
      <button className="download-pdf-btn no-pdf" onClick={handleDownloadPDF}>
        Download PDF
      </button>

      <div className="completed-exams-top">
        <p className="employee-line">
          <strong>Employee Name:</strong> {employeeName}
        </p>
        <h2 className="exam-title">{examName}</h2>
        <p className="overall-score-line">
          <strong>Overall Score:</strong> {Math.round(overallScore)}%
        </p>
        <p><strong>Date Completed: </strong>{examSession && examSession.end_time
          ? new Date(examSession.end_time).toLocaleString()
          : "Loading..."}</p>
      </div>

      {showSaveButton && (
        <button className="save-floating-btn no-pdf" onClick={handleSaveExplanations}>
          Save Explanations
        </button>
      )}

      {rows.length === 0 ? (
        <p>No rows found in completed_exams for this session.</p>
      ) : (
        <>
          {groupedData.map((block, idx) => {
            if (block.type === "H") {
              const stats = headerScores[block.text] || { total: 0, correct: 0 };
              const pct = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
              const displayPercent = Math.round(pct);

              return (
                <div className="question-block" key={idx}>
                  <h3>
                    {block.text}
                    {stats.total > 0 && (
                      <> &nbsp; {displayPercent}% ({stats.correct}/{stats.total})</>
                    )}
                  </h3>
                </div>
              );
            }
            if (block.type === "S") {
              return (
                <div className="question-block" key={idx}>
                  <p className="section-title">Section: {block.text}</p>
                </div>
              );
            }
            if (block.type === "T" || block.type === "FA") {
              const { rowData } = block;
              const rowId = rowData.id; // primary key in completed_exams
              const isFirstCorrect = !!rowData.is_correct_first;
              const isSecondCorrect = !!rowData.is_correct_second;
              const finalIncorrect = !isFirstCorrect && !isSecondCorrect;

              return (
                <div className="question-block" key={idx}>
                  {block.section && (
                    <p className="section-title">Section: {block.section}</p>
                  )}
                  {block.scenarios.map((sc, i) => (
                    <p key={i} className="scenario-line">
                      Scenario: {sc}
                    </p>
                  ))}

                  <p>
                    <strong>Question:</strong> {rowData.question_text}
                  </p>

                  {/*  Highlight first answer */}
                  <p className={`answer-line ${isFirstCorrect ? "correct" : "incorrect"}`}>
                    <strong>Answer:</strong> {rowData.user_answer_first}{" "}
                    {isFirstCorrect ? "(Correct)" : "(Incorrect)"}
                  </p>

                  {/* If T, show second attempt if first was wrong */}
                  {block.type === "T" && !isFirstCorrect && (
                    <p className={`answer-line ${isSecondCorrect ? "correct" : "incorrect"}`}>
                      <strong>2nd Answer:</strong> {rowData.user_answer_second}{" "}
                      {isSecondCorrect ? "(Correct)" : "(Incorrect)"}
                    </p>
                  )}

                  {/* If T final is still incorrect, show correct answer */}
                  {block.type === "T" && finalIncorrect && (
                    <p>
                      <strong>Correct Answer:</strong> {rowData.correct_answer}
                    </p>
                  )}

                  {/* 
                     Show the manager's explanation box if:
                       - one already exists in DB, OR
                       - the overall score is over 50% AND the final was incorrect
                  */}
                  {(managerExplanations[rowId] ||
                    (overallScore > 50 && finalIncorrect)) && (
                    <div className="explanation-field">
                      <label>
                        Trainer to explain to trainee why their answer is wrong and confirm 
                        their understanding. Record why the correct answer is what it is, below:
                      </label>
                      <textarea
                        style={{ overflow: "hidden", resize: "none" }}
                        value={managerExplanations[rowId] || ""}
                        onChange={(e) => {
                          setManagerExplanations((prev) => ({
                            ...prev,
                            [rowId]: e.target.value,
                          }));
                          autoResizeTextarea(e);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </>
      )}
    </div>
  );
}

export default CompletedExams;
